import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { CSSTransition } from "react-transition-group";
import Contain from "~components/atoms/contain/contain";
import Heading from "~components/atoms/heading/heading";
import ButtonTertiary from "~components/molecules/button/button_tertiary/button_tertiary";
import Portal from "~components/utils/portal/portal";
import useClickOutside from "~hooks/use_click_outside/use_click_outside";
import useLockedBody from "~hooks/use_locked_body/use_locked_body";
import * as transitionFade from "../../../../../styles/global_scss_modules/global_css_transition_styles/transition_fade.module.scss";
import {
  modalCardWrapperStyle,
  modalCloseButtonWrapperStyle,
  modalHeaderWrapperStyle,
  modalWrapperStyle,
} from "./modal_wrapper.module.scss";

const TRANSITION_CLASSNAMES = { ...transitionFade };

const TRANSITION_TIMEOUT = {
  enter: 200,
  exit: 200,
};

function ModalWrapper({
  children,
  isModalActive,
  callbackToCloseModal,
  small,
  extraSmall,
  modalTitle,
}) {
  const focusRef = useRef();

  const rootRef = useRef();
  const setLocked = useLockedBody();

  useEffect(() => {
    setLocked(isModalActive);
  }, [isModalActive, setLocked]);

  const handleEsc = useCallback(
    (event) => {
      if (event.keyCode === 27) {
        return callbackToCloseModal();
      }
      return null;
    },
    [callbackToCloseModal]
  );

  useClickOutside(rootRef, callbackToCloseModal);

  useEffect(() => {
    if (focusRef.current) {
      focusRef.current.focus();
    }
  });

  useEffect(() => {
    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [handleEsc]);

  return (
    <Portal portalId="modal">
      <CSSTransition
        timeout={TRANSITION_TIMEOUT}
        classNames={TRANSITION_CLASSNAMES}
        in={isModalActive}
        appear
        unmountOnExit
      >
        <div className={modalWrapperStyle}>
          <Contain small={small} extraSmall={extraSmall}>
            <div ref={rootRef} className={modalCardWrapperStyle}>
              <div className={modalHeaderWrapperStyle}>
                {modalTitle && (
                  <Heading visualLevel={4} color="white">
                    {modalTitle}
                  </Heading>
                )}
                <div className={modalCloseButtonWrapperStyle}>
                  <ButtonTertiary
                    trailingIcon="icon_clear"
                    color="white"
                    isUpperCase
                    onClick={callbackToCloseModal}
                    title="Close"
                  />
                </div>
              </div>
              {children}
            </div>
          </Contain>
        </div>
      </CSSTransition>
    </Portal>
  );
}

ModalWrapper.propTypes = {
  children: PropTypes.node,
  isModalActive: PropTypes.bool,
  callbackToCloseModal: PropTypes.func,
  small: PropTypes.bool,
  extraSmall: PropTypes.bool,
  modalTitle: PropTypes.string,
};

ModalWrapper.defaultProps = {
  children: null,
  isModalActive: false,
  callbackToCloseModal: null,
  small: false,
  extraSmall: false,
  modalTitle: null,
};

export default ModalWrapper;
